import { useMutation } from '@apollo/client/react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import { GET_PROPERTY_APPRAISABLE } from '@modules/_shared/components/appraisal/appraisalQueries';
import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { UPDATE_PROPERTY } from '@modules/_shared/components/property-form/propertyFormQueries';
import { useLocale } from '@shared/hooks/locale';

export type PropertyAddressFormData = {
  street_number?: string | null;
  route?: string | null;
  postcode?: string | null;
  locality?: string | null;
  state?: string | null;
  country?: string | null;
  country_code?: string | null;
  lat?: number | null;
  lng?: number | null;
};

type PropertyAddressDialogProps = {
  open: boolean;
  propertyId: string;
  defaultAddress: PropertyAddressFormData;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

export const PropertyAddressDialog = ({
  open,
  propertyId,
  defaultAddress,
  onClose,
  onSaved,
}: PropertyAddressDialogProps) => {
  const { t, countryCode } = useLocale();
  const [updateProperty] = useMutation(UPDATE_PROPERTY, {
    refetchQueries: [
      { query: GET_PROPERTY_APPRAISABLE, variables: { propertyId } },
    ],
    awaitRefetchQueries: true,
  });

  const formDefinition: FormDefinitionType<PropertyAddressFormData> = () => [
    {
      label: t('Address'),
      type: 'address',
      gridProps: { xs: 12 },
      countryRestriction: defaultAddress.country_code ?? countryCode,
    },
  ];

  const handleSubmit = async (values: PropertyAddressFormData) => {
    await updateProperty({
      variables: {
        id: propertyId,
        property: {
          street_number: values.street_number,
          route: values.route,
          postcode: values.postcode,
          locality: values.locality,
          state: values.state,
          country: values.country,
          country_code: values.country_code,
          lat: values.lat,
          lng: values.lng,
        },
      },
    });
    await onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Address')}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <RaForm
          formDefinition={formDefinition}
          defaultValues={defaultAddress}
          onSubmit={handleSubmit}
          onCancel={onClose}
          submitButtonText={t('Save')}
        />
      </DialogContent>
    </Dialog>
  );
};
